// Phase 49 — Staff Portal in-app real-time notifications.
//
// Owns the bell icon + dropdown shown in the sticky StaffPortal header,
// the live `EventSource` connection to `/api/staff-portal/notifications/
// stream`, the unread badge, the audio "ding" (synthesised via Web Audio
// so there's no file to ship + license-clean), and the persisted mute
// toggle in localStorage.
//
// Auto-reconnect with exponential backoff on disconnect. A subtle
// "Reconnecting…" pill renders inside the bell when the stream's been
// down >30s so the staff knows they may be looking at a stale feed.
//
// Autoplay-unlock pattern: browsers block sound on tabs the user hasn't
// touched. We arm a one-time `pointerdown` listener on first mount which
// resumes the AudioContext on the FIRST interaction the staff makes.
// All visuals fire regardless of audio availability.
import { useEffect, useRef, useState, useCallback } from "react";
import { Bell, BellOff, Plus, Pencil, X as XIcon, ExternalLink, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";
import { staffApi, STAFF_TOKEN_KEY } from "@/lib/staffApi";

const MUTE_KEY = "jbk_staff_notif_muted";
const RECONNECT_BASE_MS = 1500;
const RECONNECT_MAX_MS = 30000;
const STALE_THRESHOLD_MS = 30000;

function iconFor(type) {
  if (type === "job_created") return <Plus className="h-4 w-4 text-emerald-600" />;
  if (type === "job_updated") return <Pencil className="h-4 w-4 text-amber-600" />;
  if (type === "job_deleted") return <XIcon className="h-4 w-4 text-rose-600" />;
  return <Bell className="h-4 w-4 text-muted-foreground" />;
}

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const ts = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - ts);
    const s = Math.floor(diff / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.floor(s / 60);
    if (m < 60) return `${m} min ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  } catch { return ""; }
}

export default function StaffNotificationBell({ onEvent }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTE_KEY) === "1");
  const [reconnecting, setReconnecting] = useState(false);
  const [open, setOpen] = useState(false);

  // Refs for things that must NOT trigger re-renders.
  const audioCtxRef = useRef(null);
  const audioReadyRef = useRef(false);
  const esRef = useRef(null);
  const backoffRef = useRef(RECONNECT_BASE_MS);
  const lastEventAtRef = useRef(Date.now());
  const reconnectTimerRef = useRef(null);
  const staleCheckRef = useRef(null);
  const mountedRef = useRef(true);

  // ---- Audio unlock + ding synthesiser -------------------------------
  const playDing = useCallback(() => {
    if (muted) return;
    const ctx = audioCtxRef.current;
    if (!ctx || !audioReadyRef.current) return;
    try {
      const now = ctx.currentTime;
      // Two-tone pleasant ding — high then low, short fade.
      const tones = [
        { freq: 880, start: 0,    dur: 0.10 },
        { freq: 660, start: 0.10, dur: 0.18 },
      ];
      for (const t of tones) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(t.freq, now + t.start);
        gain.gain.setValueAtTime(0.0001, now + t.start);
        gain.gain.exponentialRampToValueAtTime(0.18, now + t.start + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + t.start + t.dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(now + t.start);
        osc.stop(now + t.start + t.dur + 0.02);
      }
    } catch { /* autoplay still blocked — silently swallow */ }
  }, [muted]);

  useEffect(() => {
    const unlock = () => {
      try {
        if (!audioCtxRef.current) {
          const Ctor = window.AudioContext || window.webkitAudioContext;
          if (Ctor) audioCtxRef.current = new Ctor();
        }
        if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
          audioCtxRef.current.resume();
        }
        audioReadyRef.current = true;
      } catch { /* ignore */ }
    };
    window.addEventListener("pointerdown", unlock, { once: true });
    window.addEventListener("keydown", unlock, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  // ---- Initial load --------------------------------------------------
  const loadHistory = useCallback(async () => {
    try {
      const { data } = await staffApi.get("/staff-portal/notifications", { params: { limit: 10 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // ---- SSE stream + reconnect ---------------------------------------
  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    const token = localStorage.getItem(STAFF_TOKEN_KEY);
    if (!token) return;
    try {
      // Close any stale handle defensively (prevents double-stream when
      // we cycle through quick reconnects).
      if (esRef.current) { try { esRef.current.close(); } catch { /* */ } }
      const url = `${API_BASE}/staff-portal/notifications/stream?token=${encodeURIComponent(token)}`;
      const es = new EventSource(url);
      esRef.current = es;

      es.addEventListener("open", () => {
        lastEventAtRef.current = Date.now();
        backoffRef.current = RECONNECT_BASE_MS;
        setReconnecting(false);
      });

      es.addEventListener("notification", (ev) => {
        lastEventAtRef.current = Date.now();
        let payload = null;
        try { payload = JSON.parse(ev.data); } catch { return; }
        // Prepend + bump unread (cap visible list at 10).
        setItems((prev) => [payload, ...prev].slice(0, 10));
        setUnread((u) => u + 1);
        playDing();
        // Toast — matches existing sonner styling used across the portal.
        const verb = ({
          job_created: "New job",
          job_updated: "Job update",
          job_deleted: "Job cancelled",
        })[payload.type] || "Update";
        toast(verb, {
          description: payload.message,
          duration: 6000,
        });
        // Let the parent refresh the jobs view so the new/updated/removed
        // booking surfaces in the day list without a manual refresh.
        onEvent && onEvent(payload);
      });

      es.addEventListener("error", () => {
        // Browser will keep retrying its own retry interval, but we
        // ALSO drive an exponential backoff close-and-reopen so the URL
        // can pick up a refreshed token cleanly if it changed.
        try { es.close(); } catch { /* */ }
        if (!mountedRef.current) return;
        setReconnecting(true);
        const delay = backoffRef.current;
        backoffRef.current = Math.min(RECONNECT_MAX_MS, delay * 2);
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = setTimeout(() => { connect(); }, delay);
      });
    } catch {
      // EventSource construction itself failed — try again later.
      setReconnecting(true);
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = setTimeout(() => { connect(); }, RECONNECT_MAX_MS);
    }
  }, [playDing, onEvent]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    // Periodic stale-check — if we haven't heard from the server in
    // STALE_THRESHOLD_MS (no events AND no `: ping` comments) flag the
    // bell so the staff sees we may be looking at a stale feed.
    staleCheckRef.current = setInterval(() => {
      const stale = Date.now() - lastEventAtRef.current > STALE_THRESHOLD_MS;
      setReconnecting((cur) => (esRef.current ? (stale && cur) : true));
    }, 5000);
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimerRef.current);
      clearInterval(staleCheckRef.current);
      if (esRef.current) { try { esRef.current.close(); } catch { /* */ } }
    };
  }, [connect]);

  // ---- Actions -------------------------------------------------------
  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await staffApi.post(`/staff-portal/notifications/${id}/read`); } catch { /* */ }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: new Date().toISOString() })));
    setUnread(0);
    try { await staffApi.post("/staff-portal/notifications/read-all"); } catch { /* */ }
  };

  const onRowClick = (n) => {
    markRead(n.id);
    setOpen(false);
    // Scroll the matching booking card into view if it's currently rendered.
    if (n.booking_id) {
      // We added both an `id` and a `data-testid` on the StaffJobCard
      // wrapper — prefer the id selector since it survives test-id
      // renames + works with `getElementById` cheaply.
      const el = document.getElementById(`staff-job-card-${n.booking_id}`)
        || document.querySelector(`[data-testid="staff-job-${n.booking_id}"]`);
      if (el) {
        try { el.scrollIntoView({ behavior: "smooth", block: "center" }); } catch { /* */ }
        // Briefly highlight.
        el.classList.add("ring-2", "ring-accent");
        setTimeout(() => el.classList.remove("ring-2", "ring-accent"), 1500);
      }
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      const next = !m;
      localStorage.setItem(MUTE_KEY, next ? "1" : "0");
      return next;
    });
  };

  const badge = unread > 9 ? "9+" : String(unread);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="relative bg-transparent text-white border-white/40 hover:bg-white/10 hover:text-white hover:border-white/60 min-h-[40px] min-w-[40px] px-2"
          aria-label="Notifications"
          title="Notifications"
          data-testid="staff-notification-bell"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unread > 0 && (
            <span
              className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center shadow"
              data-testid="staff-notification-badge"
            >
              {badge}
            </span>
          )}
          {reconnecting && unread === 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-amber-400 animate-pulse"
              title="Reconnecting…"
              data-testid="staff-notification-reconnecting"
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-[340px] sm:w-[380px] p-0 border-border"
        data-testid="staff-notification-dropdown"
      >
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <div className="text-sm font-bold flex items-center gap-2">
            <Bell className="h-4 w-4" /> Notifications
            {reconnecting && (
              <span className="text-[10px] uppercase mono tracking-widest text-amber-600">
                · Reconnecting…
              </span>
            )}
          </div>
          {unread > 0 && (
            <button
              type="button"
              className="text-[11px] mono uppercase tracking-wider text-accent hover:underline flex items-center gap-1"
              onClick={markAllRead}
              data-testid="staff-notification-mark-all-read"
            >
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="staff-notification-empty">
              No notifications yet — you{"\u2019"}ll see manager updates here.
            </div>
          ) : (
            items.map((n) => (
              <button
                key={n.id}
                type="button"
                onClick={() => onRowClick(n)}
                className={`w-full text-left px-3 py-2.5 border-b border-border/60 last:border-b-0 hover:bg-secondary/40 flex items-start gap-2 ${n.read_at ? "opacity-65" : "bg-accent/5"}`}
                data-testid={`staff-notification-row-${n.id}`}
              >
                <span className="mt-0.5 shrink-0">{iconFor(n.type)}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold truncate flex items-center gap-1">
                    {n.customer_name || "Customer"}
                    {n.booking_code && (
                      <span className="mono text-[10px] uppercase text-muted-foreground">
                        · {n.booking_code}
                      </span>
                    )}
                  </div>
                  <div className="text-[12px] text-foreground/80 break-words">
                    {n.message}
                  </div>
                  <div className="text-[10px] mono uppercase tracking-wider text-muted-foreground mt-0.5 flex items-center gap-1">
                    <span>{timeAgo(n.created_at)}</span>
                    {n.booking_id && (
                      <span className="inline-flex items-center gap-0.5">
                        · open <ExternalLink className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
        <div className="border-t border-border px-3 py-2 flex items-center justify-between">
          <button
            type="button"
            onClick={toggleMute}
            className="flex items-center gap-2 text-[12px] text-foreground hover:underline"
            data-testid="staff-notification-mute-toggle"
            aria-pressed={muted}
          >
            {muted ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
            {muted ? "Sound muted" : "Mute notification sound"}
          </button>
          <span className="text-[10px] mono uppercase tracking-widest text-muted-foreground">
            last 10
          </span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
